import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, ExternalLink, Loader2, Send, Share2, ShieldOff } from "lucide-react";

export default function ProcessShareDialog({ proc, open, onOpenChange }) {
  const [link, setLink] = useState("");
  const [emails, setEmails] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.post(`/processes/${proc.id}/share`)
      .then(({ data }) => setLink(`${window.location.origin}/shared/process/${data.token}`))
      .catch(() => toast.error("Couldn't create the share link"));
  }, [open, proc.id]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); toast.success("Link copied to clipboard"); }
    catch { toast.error("Copy failed — select the link and copy it manually"); }
  };

  const send = async () => {
    const list = emails.split(/[,;\s]+/).filter(Boolean);
    if (!list.length) return toast.error("Add at least one email address");
    setSending(true);
    try {
      const { data } = await api.post(`/processes/${proc.id}/share-email`, { emails: list, note });
      if (data.sent) { toast.success(`Link sent to ${data.sent} recipient${data.sent === 1 ? "" : "s"}`); setEmails(""); setNote(""); }
      else toast.error(data.error || "Email could not be sent");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Email could not be sent");
    } finally { setSending(false); }
  };

  const revoke = async () => {
    setRevoking(true);
    try {
      await api.delete(`/processes/${proc.id}/share`);
      setLink("");
      toast.success("Share link revoked — old links no longer work");
      onOpenChange(false);
    } catch { toast.error("Couldn't revoke the link"); }
    finally { setRevoking(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="process-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Share2 className="h-4 w-4 text-primary" /> Share "{proc.name}"</DialogTitle>
          <DialogDescription>Anyone with the link can view a read-only copy of this process diagram — no login needed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Share link</Label>
            <div className="flex gap-2 mt-1.5">
              <Input readOnly value={link} placeholder="Creating link…" className="text-xs" data-testid="process-share-link" />
              <Button variant="outline" size="icon" onClick={copy} disabled={!link} title="Copy link" data-testid="process-share-copy"><Copy className="h-4 w-4" /></Button>
              <Button variant="outline" size="icon" onClick={() => window.open(link, "_blank")} disabled={!link} title="Open in a new tab" data-testid="process-share-open"><ExternalLink className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="border-t border-border pt-4 space-y-3">
            <div>
              <Label>Email the link to stakeholders</Label>
              <Input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="name@company.com, other@company.com" className="mt-1.5" data-testid="process-share-emails" />
            </div>
            <div>
              <Label>Note <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Please review our updated flow" className="mt-1.5" data-testid="process-share-note" />
            </div>
            <Button className="w-full gap-2" onClick={send} disabled={sending || !link} data-testid="process-share-send">
              {sending ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <><Send className="h-4 w-4" /> Send the link</>}
            </Button>
          </div>
          <button type="button" onClick={revoke} disabled={revoking || !link}
            className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
            data-testid="process-share-revoke">
            {revoking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldOff className="h-3.5 w-3.5" />} Revoke this link (stops all shared access)
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
